import {Controller, useFormContext} from 'react-hook-form';
import {NetworkDetailsFormValues, NetworkNodeFieldPath} from '../../../types';
import AppMUIAutocomplete from '../../../../../../../components/common/AppMUIAutoComplete';
import {COLORS, ICONS} from '../../../../../../users/data';
import {
  PERMISSIONS_LEVELS,
  PERMISSIONS_LIST,
} from '../../../data/permissionsList';
import AppMenu from '../../../../../../../components/common/AppMenu';
import {SVG} from '../../../../../../../constants/assetLists';
import PermissionToggleCell from './PermissionToggleCell';
import {useOnBoardingContext} from '../../../../../../../context/onBoarding/OnBoardingContextProvider';

const MENUITEMS = [
  {
    title: 'שכפול',
    value: 'copy',
    icon: SVG.copyGrayIcon,
  },
  {
    title: 'מחיקה',
    value: 'delete',
    icon: SVG.deleteGrayIcon,
  },
];

type RoleRowProps = {
  selectedFieldPath: NetworkNodeFieldPath;
  roleIndex: number;
  openMenuRoleId: string | null;
  menuAnchorEl: HTMLElement | null;
  onMenuOpen: (roleId: string, event: React.MouseEvent<HTMLElement>) => void;
  onMenuClose: () => void;
  onMenuItemClick: (roleIndex: number, value: string) => void;
  displayBorder?: boolean;
};

function RoleRow({
  selectedFieldPath,
  roleIndex,
  openMenuRoleId,
  menuAnchorEl,
  onMenuOpen,
  onMenuClose,
  onMenuItemClick,
  displayBorder = true,
}: RoleRowProps) {
  const {control, watch, setValue} = useFormContext<NetworkDetailsFormValues>();
  const roleId = watch(`${selectedFieldPath}.roles.${roleIndex}.id`);
  const rolePath = `${selectedFieldPath}.roles.${roleIndex}` as const;
  const {allRoleTypes} = useOnBoardingContext();

  const borderClass = displayBorder ? 'border-y border-y-gray70' : 'border-t border-t-gray70';

  return (
    <tr>
      <td
        className={`h-10 ${borderClass} px-2 py-1`}
        style={{width: '200px', maxWidth: '200px'}}>
        <div className="w-full h-full">
          <Controller
            name={`${rolePath}.color`}
            control={control}
            render={({field: colorField, fieldState: {error}}) => (
              <AppMUIAutocomplete
                disablePortal={false}
                size="small"
                options={COLORS}
                fullWidth
                disableClearable
                getOptionLabel={() => ''}
                slotProps={{
                  listbox: {
                    sx: {
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: 1,
                      p: 1,
                      maxHeight: 320,
                      overflowY: 'auto',
                      '& .MuiAutocomplete-option': {
                        p: '6px',
                        '&.Mui-focused, &[aria-selected="true"]': {
                          backgroundColor: 'transparent !important',
                        },
                      },
                    },
                  },
                }}
                renderValue={(value, getItemProps) =>
                  value ? (
                    <div
                      {...getItemProps()}
                      className="flex items-center gap-2">
                      <div
                        className="w-[20px] h-[20px] rounded-[3px] border-1 border-gray"
                        style={{backgroundColor: value?.color}}
                      />
                      <span className="text-base text-textColor">צבע</span>
                    </div>
                  ) : null
                }
                renderOption={(props, option) => {
                  const {key, ...optionProps} = props;
                  const isSelected = colorField.value === option?.color;
                  return (
                    <li key={key} {...optionProps}>
                      <div
                        className="w-[20px] h-[20px] rounded-[3px]"
                        style={{
                          backgroundColor: option?.color,
                          border: isSelected
                            ? '2px solid #333333'
                            : '1px solid #333333',
                        }}
                      />
                    </li>
                  );
                }}
                value={
                  COLORS.find(item => item?.color === colorField.value) ??
                  undefined
                }
                onChange={(_event, option) =>
                  colorField.onChange(option?.color ?? '')
                }
                onBlur={colorField.onBlur}
                error={!!error?.message}
                helperText={error?.message}
              />
            )}
          />
        </div>
      </td>

      <td
        className={`h-10 ${borderClass} px-2 py-1`}
        style={{width: '400px', maxWidth: '400px'}}>
        <div className="w-full h-full flex items-center gap-1.5  ">
          <div className="w-[200px]">
            <Controller
              name={`${rolePath}.icon`}
              control={control}
              render={({field: iconField, fieldState: {error}}) => (
                <AppMUIAutocomplete
                  key={iconField.value}
                  disablePortal={false}
                  size="small"
                  options={ICONS}
                  getOptionLabel={o => o.id}
                  fullWidth
                  disableClearable
                  slotProps={{
                    listbox: {
                      sx: {
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 1,
                        p: 1,
                        maxHeight: 320,
                        overflowY: 'auto',
                        '& .MuiAutocomplete-option': {
                          p: '8px',
                        },
                      },
                    },
                  }}
                  renderValue={value =>
                    value && (
                      <div className="flex items-center gap-2">
                        <img
                          src={value?.icon}
                          alt="icon"
                          className="w-[20px] h-[20px] object-contain"
                        />
                        <span className="text-base text-textColor">איקון</span>
                      </div>
                    )
                  }
                  renderOption={(props, option) => {
                    const {key, ...optionProps} = props;

                    return (
                      <li key={key} {...optionProps}>
                        <img
                          src={option?.icon}
                          alt="icon"
                          className={`w-[20px] h-[20px] object-contain`}
                        />
                      </li>
                    );
                  }}
                  value={
                    ICONS.find(item => item?.id === iconField.value) ??
                    undefined
                  }
                  onChange={(_event, option) =>
                    iconField.onChange(option?.id ?? '')
                  }
                  onBlur={iconField.onBlur}
                  error={!!error?.message}
                  helperText={error?.message}
                />
              )}
            />
          </div>

          <div className="w-[200px]">
            <Controller
              name={`${rolePath}.roleType`}
              control={control}
              render={({field: roleTypeField, fieldState: {error}}) => {
                const selectedRoleTypeObj = allRoleTypes?.data?.find(
                  item => item?._id === roleTypeField.value,
                );
                const autocompleteValue =
                  selectedRoleTypeObj || roleTypeField.value || '';

                return (
                  <AppMUIAutocomplete
                    disablePortal={false}
                    size="small"
                    fullWidth
                    freeSolo
                    disableClearable
                    options={allRoleTypes?.data || []}
                    getOptionLabel={(
                      option:
                        | {
                            _id?: string;
                            name?: {he?: string};
                            icon?: string;
                          }
                        | string,
                    ) => {
                      if (typeof option === 'string') return option;
                      return option?.name?.he || '';
                    }}
                    value={autocompleteValue}
                    onChange={(_e, val) => {
                      if (val && typeof val === 'object' && val?._id) {
                        roleTypeField.onChange(val._id);
                        if (val?.icon) {
                          setValue(`${rolePath}.icon`, val.icon, {
                            shouldValidate: true,
                            shouldTouch: true,
                            shouldDirty: true,
                          });
                        }
                      } else if (typeof val === 'string') {
                        roleTypeField.onChange(val);
                      } else {
                        roleTypeField.onChange('');
                      }
                    }}
                    onInputChange={(_e, newInputValue, reason) => {
                      if (reason === 'input') {
                        const matchingRoleType = allRoleTypes?.data?.find(
                          item => item?.name?.he === newInputValue,
                        );
                        if (matchingRoleType) {
                          roleTypeField.onChange(matchingRoleType._id);
                          if (matchingRoleType?.icon) {
                            setValue(
                              `${rolePath}.icon`,
                              matchingRoleType.icon,
                              {
                                shouldValidate: true,
                                shouldTouch: true,
                                shouldDirty: true,
                              },
                            );
                          }
                        } else {
                          roleTypeField.onChange(newInputValue);
                        }
                      }
                    }}
                    onBlur={roleTypeField.onBlur}
                    error={!!error?.message}
                    helperText={error?.message}
                  />
                );
              }}
            />
          </div>
        </div>
      </td>

      <td
        className={`h-10 ${borderClass} px-2 py-1`}
        style={{width: '200px', maxWidth: '200px'}}>
        <div className="w-full h-full flex items-center gap-2">
          <div className="w-[200px]">
            <Controller
              name={`${rolePath}.permissionLevel`}
              control={control}
              render={({field: levelField, fieldState: {error}}) => (
                <AppMUIAutocomplete
                  disablePortal={false}
                  size="small"
                  fullWidth
                  disableClearable
                  options={PERMISSIONS_LEVELS}
                  getOptionLabel={option => option?.name || ''}
                  value={
                    PERMISSIONS_LEVELS.find(l => l.id === levelField.value) ??
                    undefined
                  }
                  onChange={(_event, option) => {
                    levelField.onChange(option?.id ?? '');
                    setValue(
                      `${selectedFieldPath}.roles.${roleIndex}.permissions`,
                      option?.permissions || [],
                      {
                        shouldValidate: true,
                        shouldTouch: true,
                        shouldDirty: true,
                      },
                    );
                  }}
                  onBlur={levelField.onBlur}
                  error={!!error?.message}
                  helperText={error?.message}
                />
              )}
            />
          </div>

          <AppMenu
            anchorEl={openMenuRoleId === roleId ? menuAnchorEl : null}
            open={openMenuRoleId === roleId && Boolean(menuAnchorEl)}
            onOpen={event => onMenuOpen(roleId, event)}
            onClose={onMenuClose}
            items={MENUITEMS}
            onItemClick={value => onMenuItemClick(roleIndex, value)}
          />
        </div>
      </td>

      <td
        className={`h-10 ${borderClass}`}
        style={{width: '72px', maxWidth: '72px'}}>
        <div className="h-full flex justify-center items-center">
          <Controller
            name={`${rolePath}.customPermission`}
            control={control}
            render={({field: customField}) => (
              <button
                type="button"
                className="w-6 h-6 rounded-lg hover:bg-pastelBlue transition-all duration-300 flex justify-center items-center"
                onClick={() => customField.onChange(!customField.value)}>
                <img
                  src={
                    customField.value ? SVG?.tickGreenIcon : SVG.closeDarkIcon
                  }
                  alt="icon"
                  className={`${customField.value ? 'w-[18px] h-[18px]' : 'w-[12px] h-[12px]'} object-contain`}
                />
              </button>
            )}
          />
        </div>
      </td>

      {PERMISSIONS_LIST.flatMap(group =>
        group.permissions.map((permission, idx) => (
          <PermissionToggleCell
            key={`${group.group}-${permission.id}-${idx}`}
            selectedFieldPath={selectedFieldPath}
            roleIndex={roleIndex}
            permissionId={permission.id}
            showGroupBorder={idx === 0}
            displayBorder={displayBorder}
          />
        )),
      )}
    </tr>
  );
}

export default RoleRow;
